import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField, Button, Divider, Stack, Snackbar, Alert, Typography } from '@mui/material';
import { getDb, getAuth, waitForFirebase } from '../services/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDocs, query, where } from 'firebase/firestore';
import GroupManager from './GroupManager';
import JoinGroupDialog from './JoinGroupDialog';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification, fetchSignInMethodsForEmail } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import TeamDialog from './TeamDialog';
import GoogleIcon from '@mui/icons-material/Google';

interface RegisterDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (form: any) => void;
  loading?: boolean;
  onShowLogin?: () => void;
}

const RegisterDialog: React.FC<RegisterDialogProps> = ({ open, onClose, onSubmit, loading, onShowLogin }) => {
  const { isAuthenticated, user, handleGoogleLogin } = useAuth();
  const [form, setForm] = React.useState({
    email: '',
    password: '',
    lastName: '',
    firstName: ''
  });
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [showJoinGroup, setShowJoinGroup] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'error' });
  const [showConfirm, setShowConfirm] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [teamList, setTeamList] = useState<string[]>([]);
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailExists, setEmailExists] = useState(false);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [emailCheckError, setEmailCheckError] = useState<string | null>(null);
  let emailCheckTimeout: NodeJS.Timeout;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Email 轉小寫
      const email = form.email.trim().toLowerCase();
      // 等待 Firebase 初始化
      await waitForFirebase();
      const auth = getAuth();
      if (!auth) {
        throw new Error('Firebase Auth 未初始化');
      }
      
      // 建立用戶
      const userCredential = await createUserWithEmailAndPassword(auth, email, form.password);
      const user = userCredential.user;

      // 更新用戶資料
      await updateProfile(user, {
        displayName: `${form.lastName} ${form.firstName}`
      });

      // 🔧 新增：在 users 集合中創建用戶記錄
      try {
        const { createUser } = await import('../services/userService');
        await createUser({
          uid: user.uid,
          email: user.email || '',
          firstName: form.firstName,
          lastName: form.lastName
        });
        console.log('✅ 用戶記錄已創建到 users 集合');
      } catch (userError) {
        console.warn('⚠️ 創建用戶記錄失敗，但註冊繼續:', userError);
      }

      // 發送驗證信
      await sendEmailVerification(user, {
        url: `${window.location.origin}/verify-email`,
        handleCodeInApp: true
      });

      // 顯示成功訊息
      toast.success('註冊成功！請查收驗證信。');
      
      // 關閉對話框
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error: any) {
      console.error('註冊失敗:', error);
      let errorMessage = '註冊失敗，請重試';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = '此 Email 已被註冊';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = '密碼強度不足';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = '無效的 Email 格式';
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateTeam = () => {
    if (!isAuthenticated) {
      setSnackbar({ open: true, message: '請先登入CheckPlay', severity: 'error' });
      return;
    }
    setShowTeamDialog(true);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm(f => ({ ...f, email: value }));
    setEmailExists(false);
    setEmailCheckError(null);
    if (emailCheckTimeout) clearTimeout(emailCheckTimeout);
    if (!value) return;
    setEmailCheckLoading(true);
    emailCheckTimeout = setTimeout(async () => {
      try {
        const methods = await fetchSignInMethodsForEmail(auth, value);
        setEmailExists(methods.length > 0);
        setEmailCheckError(null);
      } catch (err: any) {
        setEmailExists(false);
        setEmailCheckError('Email 格式錯誤或查詢失敗');
      } finally {
        setEmailCheckLoading(false);
      }
    }, 500);
  };

  useEffect(() => {
    console.log('RegisterDialog open:', open);
  }, [open]);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!isAuthenticated || !user?.email) {
        setIsAdmin(false);
        setTeamList([]);
        return;
      }
      
      // 等待 Firebase 初始化
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        console.warn('⚠️ Firestore 未初始化');
        return;
      }
      
      const q = query(
        collection(db, 'groups'),
        where('admins', 'array-contains', user.email)
      );
      const snap = await getDocs(q);
      setIsAdmin(!snap.empty);
      if (!snap.empty) {
        setTeamList(snap.docs.map(doc => doc.data().name || '未知群組'));
      } else {
        setTeamList([]);
      }
    };
    checkAdmin();
  }, [isAuthenticated, user]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
        PaperProps={{
          style: {
            borderRadius: 28,
            width: 440,
            maxWidth: '95vw',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 800, fontSize: 28, color: '#1976d2', letterSpacing: 1, pt: 4, pb: 1 }}>
          加入 <span style={{ color: '#FF0000' }}>CheckPlay</span> 簡播
        </DialogTitle>
        <DialogContent sx={{ px: 5, pt: 0, pb: 2 }}>
          <Box sx={{ mt: 1, color: 'text.secondary', fontSize: 6, textAlign: 'center', letterSpacing: 0.5 }} />
          <Box sx={{ mt: 1, color: 'text.secondary', fontSize: 13, textAlign: 'center', letterSpacing: 0.5 }}>
            <span style={{ color: '#1976d2', fontWeight: 700 }}>免費會員：</span>影片收藏、重點標記、分享功能<br />
            <span style={{ color: '#FF0000', fontWeight: 700 }}>付費會員：</span>團隊協作、進階統計、專業支援等
          </Box>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField 
              label="Email" 
              type="email" 
              fullWidth 
              size="medium"
              value={form.email}
              onChange={handleEmailChange}
              required
              error={emailExists || !!emailCheckError}
              helperText={
                emailCheckLoading ? '檢查中...'
                : emailExists ? '此 Email 已被註冊，請改用其他 Email'
                : emailCheckError ? emailCheckError
                : ''
              }
            />
            <TextField 
              label="密碼" 
              type="password" 
              fullWidth 
              size="medium"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              required
              helperText="密碼至少需要 6 個字元"
            />
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField 
                label="姓 (Last Name)" 
                fullWidth 
                size="medium"
                value={form.lastName}
                onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                required
              />
              <TextField 
                label="名 (First Name)" 
                fullWidth 
                size="medium"
                value={form.firstName}
                onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                required
              />
            </Box>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              fullWidth 
              sx={{ fontSize: 18, borderRadius: 2, fontWeight: 800, py: 1.2 }} 
              disabled={isSubmitting}
            >
              {isSubmitting ? '註冊中...' : '註冊帳號'}
            </Button>
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                用Google ID 快速加入免註冊
              </Typography>
            </Divider>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon sx={{ color: '#EA4335', fontSize: 28 }} />}
              sx={{
                mb: 2,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: 17,
                color: '#444',
                borderColor: '#EA4335',
                '&:hover': { borderColor: '#EA4335', background: '#fbe9e7' }
              }}
              onClick={async (e) => {
                e.preventDefault();
                try {
                  await handleGoogleLogin();
                  onClose();
                } catch (err) {
                  alert('Google 登入失敗，請稍後再試');
                }
              }}
              disabled={loading}
            >
              使用 Google ID 登入
            </Button>
          </Box>
          {/* <Divider sx={{ my: 2, fontSize: 15, color: '#888', fontWeight: 700, borderColor: '#e0e0e0' }}>已註冊者，請快速登入</Divider>
          <Stack direction="column" spacing={1.5}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              sx={{ borderRadius: 2, fontWeight: 700, fontSize: 16, py: 1 }}
              onClick={() => {
                console.log('RegisterDialog EMAIL 登入按鈕被點擊');
                if (onShowLogin) onShowLogin();
              }}
              disabled={isAuthenticated}
            >
              EMAIL 登入
            </Button>
          </Stack>
          */}
          
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 0 }}>
          <Button onClick={onClose} color="primary" sx={{ borderRadius: 2, fontWeight: 700, fontSize: 16, px: 4, py: 1 }}>關閉</Button>
        </DialogActions>
      </Dialog>
      <TeamDialog open={showTeamDialog} onClose={() => setShowTeamDialog(false)} />
      <JoinGroupDialog open={showJoinGroup} onClose={() => setShowJoinGroup(false)} />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog open={showGroupManager} onClose={() => setShowGroupManager(false)} maxWidth="md" fullWidth>
        <GroupManager 
          open={showGroupManager}
          onClose={() => setShowGroupManager(false)}
        />
      </Dialog>
    </>
  );
};

export default RegisterDialog; 